/**
 * The `.prz-*` chip family from `shared/modals.css` — the marks a Prize Tier
 * wears on both prize surfaces (CAE-1575).
 *
 * One file rather than four, because they are one vocabulary: a tier is drawn
 * as a badge, a type, a value and its adapter keys, and the four only ever
 * appear together. The Contest Overview's read-only ladder is the first caller;
 * CAE-1554's Edit › Prizes renders the same row with its cells made editable,
 * which is why these are in the kit rather than beside the first of them.
 */
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import { adminTokens, type AdminTokens } from '../../theme';
import { PRIZE_TIER_TYPE_OPTIONS } from '../../config/prize-tier-types';

const prizeTypeLabelByValue = new Map(
  PRIZE_TIER_TYPE_OPTIONS.map((option) => [option.value, option.label]),
);

/*
 * Read for its *keys* only — which prize types the token layer paints. The
 * values still come off the live theme inside `sx`, so a theme override is not
 * bypassed here.
 */
const PRIZE_TYPE_PALETTES = adminTokens.componentColor.prizeTypePill;

type PrizeTypePaletteKey = keyof AdminTokens['componentColor']['prizeTypePill'];

/**
 * Which palette a prize type claims.
 *
 * Both halves of the test matter, and they are different questions. The type
 * has to be one this deployment **configures** — `PRIZE_TIER_TYPE_OPTIONS` is
 * per-deployment config, so a value dropped from it must stop wearing its old
 * colour rather than keep a palette it no longer has a label for. And it has to
 * be one the token layer actually **paints**, so a type added to the config
 * without a palette lands on the neutral wash instead of reading an undefined
 * entry.
 *
 * A stored `prize_type` of "unknown" needs no special case: it reaches the
 * fallback palette through whichever of the two tests it fails, and the
 * fallback is the palette it would land on anyway.
 */
const prizeTypePaletteKey = (prizeType: string): PrizeTypePaletteKey => {
  if (!prizeTypeLabelByValue.has(prizeType)) return 'unknown';
  return prizeType in PRIZE_TYPE_PALETTES ? (prizeType as PrizeTypePaletteKey) : 'unknown';
};

/**
 * `.prz-typepill` — the prize type, keyed on its own value.
 *
 * A type outside the configured list keeps its stored string and takes the
 * neutral wash. `prize_type` is free text in the database, so this is a row an
 * operator can really have; the one thing the chip must not do is hide which
 * value is in it.
 */
export const PrizeTypePill = ({ prizeType }: { prizeType: string }) => {
  const label = prizeTypeLabelByValue.get(prizeType);

  return (
    <Box
      component="span"
      sx={(theme: Theme) => {
        const palette = theme.tokens.componentColor.prizeTypePill[prizeTypePaletteKey(prizeType)];

        return {
          display: 'inline-flex',
          alignItems: 'center',
          padding: '3px 10px',
          borderRadius: `${theme.tokens.radius.badge}px`,
          fontSize: '12px',
          fontWeight: 600,
          letterSpacing: '0.01em',
          border: `0.5px solid ${palette.border}`,
          backgroundColor: palette.bg,
          color: palette.text,
        };
      }}
    >
      {label ?? prizeType}
    </Box>
  );
};

/** `.prz-segpill` — a segment's name, set in mono because a ref can be a UUID. */
export const SegmentPill = ({ label }: { label: string }) => (
  <Box
    component="span"
    sx={(theme: Theme) => ({
      display: 'inline-flex',
      alignItems: 'center',
      padding: '3px 9px',
      borderRadius: `${theme.tokens.radius.badge}px`,
      border: theme.tokens.border.default,
      backgroundColor: theme.tokens.componentColor.segmentPillBg,
      color: theme.tokens.color.ink2,
      fontFamily: theme.tokens.font.mono,
      fontSize: '12px',
      fontWeight: 500,
    })}
  >
    {label}
  </Box>
);

/**
 * `.prz-tierbadge` — the correct count a tier pays on.
 *
 * The number alone, with the row's own label ("3 correct picks") carrying the
 * unit beside it. Hidden from the reading order for that reason: announced, it
 * would read "3 3 correct picks".
 */
export const TierBadge = ({ correctCount }: { correctCount: number }) => (
  <Box
    aria-hidden
    sx={(theme: Theme) => ({
      display: 'grid',
      placeItems: 'center',
      flexShrink: 0,
      width: '36px',
      height: '36px',
      borderRadius: `${theme.tokens.radius.insetRow}px`,
      border: `0.5px solid ${theme.tokens.componentColor.tierBadgeBorder}`,
      backgroundColor: theme.tokens.color.accentSoft,
      color: theme.tokens.color.accentStrong,
      fontFamily: theme.tokens.font.mono,
      fontWeight: 700,
      fontSize: '15px',
      fontVariantNumeric: 'tabular-nums',
    })}
  >
    {correctCount}
  </Box>
);

/**
 * What a chip prints for one `adapter_config` value.
 *
 * The column is jsonb, so a value can be a number, a nested object or an
 * array. `String(…)` turns the last two into "[object Object]" and
 * "1,2" — text that looks like a reading and is not one — so anything that is
 * not already a scalar is serialised instead.
 */
const printAdapterValue = (value: unknown): string => {
  if (value === null) return 'null';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/**
 * `.prz-adapter-kv` — the key/value chips a Prize Tier's adapter config wears.
 *
 * Every key the row carries, in the order the adapter sent them. The shapes
 * differ per prize type (a FREE_BET's `amount` against a PROFIT_BOOST's three
 * fields) and the column accepts anything, so the chips are built from the
 * object rather than from a list this screen keeps.
 *
 * `max` is the collapsed peek Edit › Prizes draws inside its `<details>`
 * (CAE-1554): the first N keys, then a count of what is folded away. The
 * Contest Overview's ladder passes nothing and gets all of them, because
 * nothing there opens to show the rest.
 */
export const AdapterConfigChips = ({
  config,
  max,
}: {
  config: Record<string, unknown>;
  max?: number;
}) => {
  const allEntries = Object.entries(config);
  const entries = max === undefined ? allEntries : allEntries.slice(0, max);
  const hiddenCount = allEntries.length - entries.length;

  if (allEntries.length === 0) {
    return (
      <Box
        component="span"
        sx={(theme: Theme) => ({ fontSize: '12px', color: theme.tokens.color.ink4 })}
      >
        No adapter config
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', minWidth: 0 }}>
      {entries.map(([key, value]) => (
        <Box
          key={key}
          component="span"
          sx={(theme: Theme) => ({
            display: 'inline-flex',
            alignItems: 'stretch',
            overflow: 'hidden',
            borderRadius: `${theme.tokens.radius.adapterChip}px`,
            border: theme.tokens.border.default,
            backgroundColor: theme.tokens.color.surfaceMuted,
            fontFamily: theme.tokens.font.mono,
            fontSize: '11px',
          })}
        >
          <Box
            component="span"
            data-testid="adapter-config-key"
            sx={(theme: Theme) => ({
              padding: '1px 6px',
              backgroundColor: theme.tokens.componentColor.adapterChipKeyBg,
              color: theme.tokens.color.ink4,
              borderRight: theme.tokens.border.default,
              fontWeight: 500,
              letterSpacing: '0.02em',
            })}
          >
            {key}
          </Box>
          <Box
            component="span"
            sx={(theme: Theme) => ({
              padding: '1px 7px',
              color: theme.tokens.color.ink2,
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '-0.01em',
            })}
          >
            {printAdapterValue(value)}
          </Box>
        </Box>
      ))}
      {hiddenCount > 0 ? (
        /* `.prz-adapter-more` — the count sits in the body face, not the mono
           of the chips beside it: it is prose about the payload, not part of
           it. */
        <Box
          component="span"
          sx={(theme: Theme) => ({
            padding: '1px 6px',
            borderRadius: `${theme.tokens.radius.adapterChip}px`,
            backgroundColor: theme.tokens.componentColor.adapterChipKeyBg,
            color: theme.tokens.color.ink4,
            fontFamily: theme.tokens.font.sans,
            fontSize: '11px',
            fontStyle: 'italic',
          })}
        >
          +{hiddenCount} more
        </Box>
      ) : null}
    </Box>
  );
};
